import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ListItemIcon from '@mui/material/ListItemIcon';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MLIcon from '../MLIcon';
import MLButton from '../MLButton';
import MLMenuList from '../MLMenuList/MLMenuList';
import MLMenuItem from '../MLMenuItem/MLMenuItem';

import s from './MLMenu.module.css';

function MLMenu({
  open,
  anchorEl,
  handleOpen = () => {},
  handleClose = () => {},
  buttonLabel,
  buttonClass,
  buttonLabelClass,
  buttonTestId = 'testId',
  buttonLeftIcon = null,
  buttonRightIcon = null,
  menuItemClass,
  optionLabelClass,
  selectedIconClass,
  disableEnforceFocus,
  options = [],
  alignment = 'left',
  ariaLabel,
  closeOnTab = false,
}) {
  const buttonRef = useRef(null);

  // Handle Escape and Tab keys for Popper-based dropdown
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        handleClose();
        buttonRef.current?.focus();
      } else if (e.key === 'Tab') {
        // Just close the menu - let Tab naturally move to next element
        handleClose();
      }
    },
    [handleClose],
  );

  // Render Popper-based dropdown when closeOnTab is enabled (no focus trapping)
  if (closeOnTab) {
    return (
      <>
        <MLButton
          ref={buttonRef}
          btnType="secondary"
          className={classNames(s.menuButton, buttonClass)}
          ariaExpanded={open}
          ariaHaspopup="true"
          ariaLabel={ariaLabel}
          testId={buttonTestId}
          onClick={handleOpen}
        >
          {buttonLeftIcon && buttonLeftIcon}
          <Typography className={classNames(s.buttonLabel, buttonLabelClass)}>
            {buttonLabel}
          </Typography>
          {buttonRightIcon && buttonRightIcon}
        </MLButton>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement={alignment === 'right' ? 'bottom-end' : 'bottom-start'}
          style={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper className={s.popoverPaperMenu}>
              <MLMenuList onKeyDown={handleKeyDown}>
                {options.map((option) => (
                  <MLMenuItem
                    key={option.label}
                    aria-label={option.label}
                    data-test-id={option.testId ?? `${option.label}-menu-item`}
                    disabled={option.isDisabled}
                    onClick={option.onClick}
                    className={classNames(
                      option.selected != null && s.menuItemWithRightIcon,
                      menuItemClass,
                    )}
                  >
                    {option.icon && (
                      <ListItemIcon classes={{ root: s.listItemIcon }}>
                        <MLIcon type={option.icon} className={s.icon} />
                      </ListItemIcon>
                    )}
                    <Typography
                      className={classNames(s.optionLabel, optionLabelClass)}
                    >
                      {option.label}
                    </Typography>
                    {option.selected && (
                      <MLIcon
                        type={'check'}
                        className={classNames(
                          s.selectedIcon,
                          selectedIconClass,
                        )}
                      />
                    )}
                  </MLMenuItem>
                ))}
              </MLMenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  // Default: use Popover (original behavior)
  return (
    <>
      <MLButton
        ref={buttonRef}
        btnType="secondary"
        className={classNames(s.menuButton, buttonClass)}
        ariaExpanded={open}
        ariaLabel={ariaLabel}
        testId={buttonTestId}
        onClick={handleOpen}
      >
        {buttonLeftIcon && buttonLeftIcon}
        <Typography className={classNames(s.buttonLabel, buttonLabelClass)}>
          {buttonLabel}
        </Typography>
        {buttonRightIcon && buttonRightIcon}
      </MLButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        classes={{ paper: s.popoverPaperMenu }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: alignment,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: alignment,
        }}
        onClose={handleClose}
        disableEnforceFocus={disableEnforceFocus}
      >
        <MLMenuList>
          {options.map((option) => {
            return (
              <MLMenuItem
                key={option.label}
                aria-label={option.label}
                data-test-id={option.testId ?? `${option.label}-menu-item`}
                disabled={option.isDisabled}
                onClick={option.onClick}
                className={classNames(
                  option.selected != null && s.menuItemWithRightIcon,
                  menuItemClass,
                )}
              >
                {option.icon && (
                  <ListItemIcon
                    classes={{
                      root: s.listItemIcon,
                    }}
                  >
                    <MLIcon type={option.icon} className={s.icon} />
                  </ListItemIcon>
                )}
                <Typography
                  className={classNames(s.optionLabel, optionLabelClass)}
                >
                  {option.label}
                </Typography>
                {option.selected && (
                  <MLIcon
                    type={'check'}
                    className={classNames(s.selectedIcon, selectedIconClass)}
                  />
                )}
              </MLMenuItem>
            );
          })}
        </MLMenuList>
      </Popover>
    </>
  );
}

export default MLMenu;
